import { Box } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { toNumber } from 'shared/lib/number/number';
import { formatReportCurrency } from 'shared/lib/reportFormatters';
import { getPropertiesDisplayLabel } from 'shared/lib/reports';
import { getReportPropertiesTooltipContent } from 'shared/ui/ReportPropertiesTooltip';
import {
    ComposableReportWidget,
    type ReportWidgetColumnConfig,
    type ReportWidgetConfig,
    type ReportWidgetInteractionPayload,
    type ReportWidgetItem,
    type ReportWidgetTooltipRowConfig,
    type ReportWidgetTooltipRowsBuilder,
    augmentItemsForTopNOther,
    buildTopNChartItemsWithOther,
} from 'widgets/ComposableReportWidget';
import {
    buildSpendRow,
    getTooltipPointFromPayload,
} from 'widgets/ComposableReportWidget/model/lib/tooltipHelpers';

import type {
    SpendProductRowDto,
    SpendProductsResponseDto,
} from 'entities/ReportSpendSummary';

type SpendSummaryProductsTableColumnId =
    | 'item'
    | 'brand'
    | 'sku'
    | 'properties'
    | 'quantity'
    | 'totalSpend'
    | 'percentOfTotal'
    | 'percentSpend';

const renderPropertiesTooltipForProducts = (item: ReportWidgetItem) =>
    getReportPropertiesTooltipContent(
        item.meta?.spendProduct as SpendProductRowDto | undefined,
    );

function productToWidgetItem(
    dto: SpendProductRowDto,
    propertiesDisplay: string,
): ReportWidgetItem {
    return {
        id: `${dto.itemSku}-${dto.propertyCount}-${dto.totalQty}`,
        label: dto.itemName,
        primaryValue: toNumber(dto.totalSpend),
        secondaryValues: {
            itemName: dto.itemName,
            brand: dto.brand ?? '',
            sku: dto.itemSku,
            propertiesDisplay,
            quantity: dto.totalQty,
            totalSpend: dto.totalSpend,
            percentOfTotal: dto.percentOfTotal,
        },
        meta: { spendProduct: dto },
    };
}

interface SpendSummaryProductsSectionProps {
    products: SpendProductsResponseDto | null;
    totalSpend?: string;
    loading?: boolean;
    onProductDetailsClick?: (sku: string) => void;
    onOpenProductDrillIn?: (sku: string, product: SpendProductRowDto) => void;
    disableChartAnimation?: boolean;
    filtersKey?: string;
    showBottomBorder?: boolean;
    tableOnly?: boolean;
    tableColumnIds?: SpendSummaryProductsTableColumnId[];
    subheader?: string;
}

export const SpendSummaryProductsSection = memo(
    ({
        products,
        totalSpend,
        loading = false,
        onProductDetailsClick,
        onOpenProductDrillIn,
        disableChartAnimation = false,
        filtersKey,
        showBottomBorder = true,
        tableOnly = false,
        tableColumnIds,
        subheader,
    }: SpendSummaryProductsSectionProps) => {
        const { t } = useTranslation();

        const items: ReportWidgetItem[] = useMemo(() => {
            if (!products) return [];

            const sorted = [...products.items]
                .filter((dto) => toNumber(dto.totalSpend) > 0)
                .sort((a, b) => toNumber(b.totalSpend) - toNumber(a.totalSpend))
                .map((dto) =>
                    productToWidgetItem(dto, getPropertiesDisplayLabel(dto, t)),
                );

            if (tableOnly) {
                return sorted;
            }

            return augmentItemsForTopNOther({
                items: sorted,
                topCount: 5,
                otherChartItemId: 'spend-summary-products-other',
            });
        }, [products, t, tableOnly]);

        const chartItems: ReportWidgetItem[] = useMemo(() => {
            if (tableOnly) {
                return [];
            }

            return buildTopNChartItemsWithOther({
                items,
                totalAmount: totalSpend,
                topCount: 5,
                otherItemId: 'spend-summary-products-other',
                otherLabel: t('Other'),
            });
        }, [items, totalSpend, t, tableOnly]);

        const handleDetailsInteraction = useCallback(
            (payload: ReportWidgetInteractionPayload) => {
                const sku = (
                    payload.item.meta?.spendProduct as
                        | SpendProductRowDto
                        | undefined
                )?.itemSku;

                if (sku && onProductDetailsClick) {
                    onProductDetailsClick(sku);
                }
            },
            [onProductDetailsClick],
        );

        const handleRowInteraction = useCallback(
            (payload: ReportWidgetInteractionPayload) => {
                if (payload.item.meta?.isAggregatedOther) return;
                const dto = payload.item.meta?.spendProduct as
                    | SpendProductRowDto
                    | undefined;
                const sku = dto?.itemSku;

                if (sku && onOpenProductDrillIn) {
                    onOpenProductDrillIn(sku, dto);
                }
            },
            [onOpenProductDrillIn],
        );

        const tableColumns = useMemo<ReportWidgetColumnConfig[]>(() => {
            const allColumnsById: Record<
                SpendSummaryProductsTableColumnId,
                ReportWidgetColumnConfig
            > = {
                item: {
                    id: 'item',
                    label: t('Item'),
                    valueKey: 'itemName',
                    format: 'text',
                    align: 'left',
                    bold: true,
                    isDetailsTrigger: true,
                    minWidth: 200,
                    allowWrap: true,
                },
                brand: {
                    id: 'brand',
                    label: t('Brand'),
                    valueKey: 'brand',
                    format: 'text',
                    align: 'left',
                    minWidth: 160,
                    maxWidth: 260,
                    allowWrap: true,
                },
                sku: {
                    id: 'sku',
                    label: t('SKU'),
                    valueKey: 'sku',
                    format: 'text',
                    align: 'left',
                },
                properties: {
                    id: 'properties',
                    label: t('Properties'),
                    valueKey: 'propertiesDisplay',
                    format: 'text',
                    align: 'left',
                    minWidth: 120,
                    maxWidth: 150,
                    allowWrap: true,
                    getCellTooltip: renderPropertiesTooltipForProducts,
                },
                quantity: {
                    id: 'quantity',
                    label: t('Quantity'),
                    valueKey: 'quantity',
                    format: 'number',
                    align: 'right',
                },
                totalSpend: {
                    id: 'totalSpend',
                    label: t('spendSummary.totalSpend'),
                    valueKey: 'totalSpend',
                    format: 'currency',
                    align: 'right',
                },
                percentOfTotal: {
                    id: 'percentOfTotal',
                    label: t('spendSummary.percentOfTotal'),
                    valueKey: 'percentOfTotal',
                    format: 'percent',
                    align: 'right',
                },
                percentSpend: {
                    id: 'percentSpend',
                    label: t('spendSummary.percentSpend'),
                    valueKey: 'percentOfTotal',
                    format: 'percent',
                    align: 'right',
                },
            };

            const defaultOrder: SpendSummaryProductsTableColumnId[] = [
                'item',
                'brand',
                'sku',
                'properties',
                'quantity',
                'totalSpend',
                'percentOfTotal',
            ];

            const orderedIds = tableColumnIds?.length
                ? tableColumnIds
                : defaultOrder;

            const columns = orderedIds.map((id) => allColumnsById[id]);

            if (disableChartAnimation) {
                return columns.filter((column) => column.id !== 'properties');
            }

            return columns;
        }, [t, tableColumnIds, disableChartAnimation]);

        const config: ReportWidgetConfig = useMemo(() => {
            const tooltipRowsBuilder: ReportWidgetTooltipRowsBuilder = (
                args,
            ) => {
                if (args.aggregation?.kind === 'other') {
                    const entry = args.payload?.[0];
                    const aggregatedValue = toNumber(
                        entry?.value as number | string | null,
                    );

                    return [
                        ...args.aggregation.headerRows,
                        buildSpendRow({
                            id: 'spend',
                            label: t('spendSummary.spend'),
                            amount: aggregatedValue,
                            total: args.total,
                            valueFormatter: args.valueFormatter,
                            valueColor: entry?.color ?? 'metrics.base',
                        }),
                    ];
                }

                const resolved = getTooltipPointFromPayload(args);
                const item = resolved?.item;

                if (!item) {
                    return null;
                }

                const rows: ReportWidgetTooltipRowConfig[] = [
                    {
                        id: 'header',
                        variant: 'header',
                        label: item.label,
                        boldLabel: true,
                        mutedLabel: true,
                        marginBottom: 8,
                    },
                    buildSpendRow({
                        id: 'spend',
                        label: t('spendSummary.spend'),
                        amount: toNumber(item.primaryValue),
                        percent: item.secondaryValues?.percentOfTotal,
                        total: args.total,
                        valueFormatter: args.valueFormatter,
                        valueColor: resolved.color ?? 'metrics.base',
                    }),
                ];

                return rows;
            };

            return {
                widgetId: 'spend-summary-products',
                title: t('spendSummary.productsSectionTitle'),
                titleVariant: tableOnly ? 'section' : 'report',
                description: subheader,
                modelLabel: t('spendSummary.modelLabelItems'),
                layout: tableOnly ? 'stacked' : 'sideBySide',
                visualization: {
                    enabled: !tableOnly,
                    type: 'donut',
                    options: {
                        palette: 'neutral',
                        showLegend: false,
                        valueFormatter: formatReportCurrency,
                        tooltipRowsBuilder,
                    },
                },
                pagination: {
                    mode: 'client',
                    pageSize: 5,
                    revealAllOnLoadMore: true,
                },
                table: {
                    enabled: true,
                    columns: tableColumns,
                    options: {
                        showColorSwatchInLabelColumn: tableOnly
                            ? undefined
                            : 'itemName',
                        loadMoreVariant: 'link',
                    },
                },
                onRowClick: onOpenProductDrillIn
                    ? handleRowInteraction
                    : undefined,
                onChartItemClick:
                    !tableOnly && onOpenProductDrillIn
                        ? handleRowInteraction
                        : undefined,
                onDetailsClick: handleDetailsInteraction,
            };
        }, [
            t,
            handleDetailsInteraction,
            handleRowInteraction,
            onOpenProductDrillIn,
            tableOnly,
            tableColumns,
            subheader,
        ]);

        return (
            <Box
                data-testclass="spend-summary-products-section"
                data-pdf-section="true"
                data-pdf-force-page-break="true"
                sx={{
                    borderBottom: showBottomBorder
                        ? '1px solid #E0E0E0'
                        : 'none',
                    paddingBottom: showBottomBorder ? '40px' : 0,
                }}
            >
                <ComposableReportWidget
                    key={filtersKey}
                    config={config}
                    items={items}
                    chartItems={chartItems}
                    loading={loading}
                    isPrinting={disableChartAnimation}
                    disableChartAnimation={disableChartAnimation}
                />
            </Box>
        );
    },
);

SpendSummaryProductsSection.displayName = 'SpendSummaryProductsSection';
